import { useStudio } from "./StudioContext";

/**
 * Clickable content region in Visual Studio mode.
 * regionId examples: "home.pitch", "research.agendaIntro", "pillar:reflection"
 */
const EditableRegion = ({
  regionId,
  label,
  children,
  className = "",
  as: Tag = "div",
}) => {
  const { studioMode, activeRegion, setActiveRegion } = useStudio();

  if (!studioMode) {
    return <Tag className={className}>{children}</Tag>;
  }

  const active = activeRegion === regionId;

  return (
    <Tag className={`relative ${className}`} data-studio-region={regionId}>
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Let real links under the edit overlay still open.
          const overlay = e.currentTarget;
          overlay.style.pointerEvents = "none";
          const under = document.elementFromPoint(e.clientX, e.clientY);
          overlay.style.pointerEvents = "";
          const anchor = under?.closest?.("a");
          if (anchor?.href) {
            const target = anchor.getAttribute("target") || "_self";
            if (target === "_blank") {
              window.open(anchor.href, "_blank", "noopener,noreferrer");
            } else {
              window.location.assign(anchor.href);
            }
            return;
          }
          setActiveRegion(regionId);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setActiveRegion(regionId);
          }
        }}
        className={`absolute -inset-1 z-20 cursor-pointer rounded-lg border-2 ${
          active
            ? "border-green bg-green/10"
            : "border-dashed border-green/50 hover:border-green hover:bg-green/5"
        }`}
        aria-label={`Edit ${label || regionId}`}
        title={`Edit: ${label || regionId}`}
      />
      <span
        className={`pointer-events-none absolute left-1.5 top-1.5 z-30 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white ${
          active ? "bg-green-solid" : "bg-green-solid/80"
        }`}
      >
        {label || "Edit"}
      </span>
      <div className="relative z-10">{children}</div>
    </Tag>
  );
};

export default EditableRegion;
